// Development-only logging utility

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerMethod {
  (...args: any[]): void;
}

interface SpecializedLogger {
  debug: LoggerMethod;
}

interface NodeLogger extends SpecializedLogger {
  debug: LoggerMethod;
}

interface FlowLogger extends SpecializedLogger {
  debug: LoggerMethod;
  node: NodeLogger;
  edge: SpecializedLogger;
}

interface Logger {
  debug: LoggerMethod;
  info: LoggerMethod;
  warn: LoggerMethod;
  error: LoggerMethod;
  flow: FlowLogger;
  store: SpecializedLogger;
  performance: SpecializedLogger;
}

interface ContextualLogger extends Logger {
  // Additional method to create a sub-context
  withContext: (subContext: string) => ContextualLogger;
}

const isDevelopment: boolean = process.env.NODE_ENV === 'development';

/**
 * Extract file name from error stack trace
 */
const getCallerInfo = (): string => {
  try {
    const stack = new Error().stack;
    if (!stack) return '';
    
    // Get the 4th line of the stack (caller of logger method)
    const lines = stack.split('\n');
    const callerLine = lines[4] || lines[3] || '';
    
    // Extract file path from stack trace
    // Matches patterns like: "at FunctionName (http://localhost:5173/src/components/FileBrowser.tsx:123:45)"
    // or "at http://localhost:5173/src/components/FileBrowser.tsx:123:45"
    const match = callerLine.match(/(?:http:\/\/[^/]+)?\/src\/(.+?):\d+:\d+/);
    
    if (match && match[1]) {
      // Return just the file path relative to src/
      return match[1];
    }
    
    return '';
  } catch (error) {
    return '';
  }
};

/**
 * Format log message with context
 */
const formatMessage = (context: string | null, args: any[]): any[] => {
  if (context) {
    return [`[${context}]`, ...args];
  }
  
  // Auto-detect caller if no context provided
  const caller = getCallerInfo();
  if (caller) {
    return [`[${caller}]`, ...args];
  }
  
  return args;
};

/**
 * Create a contextual logger with optional file/component context
 */
const createLogger = (context: string | null = null): ContextualLogger => {
  return {
    debug: (...args: any[]): void => {
      if (isDevelopment) {
        console.debug('[DEBUG]', ...formatMessage(context, args));
      }
    },

    info: (...args: any[]): void => {
      if (isDevelopment) {
        console.info('[INFO]', ...formatMessage(context, args));
      }
    },

    warn: (...args: any[]): void => {
      if (isDevelopment) {
        console.warn('[WARN]', ...formatMessage(context, args));
      }
    },

    error: (...args: any[]): void => {
      // Always log errors, even in production
      console.error('[ERROR]', ...formatMessage(context, args));
    },

    // Specialized loggers for specific features
    flow: {
      debug: (...args: any[]): void => {
        if (isDevelopment) {
          const flowContext = context ? `${context}:FLOW` : 'FLOW';
          console.debug(`[${flowContext}]`, ...args);
        }
      },
      node: {
        debug: (...args: any[]): void => {
          if (isDevelopment) {
            const nodeContext = context ? `${context}:FLOW:NODE` : 'FLOW:NODE';
            console.debug(`[${nodeContext}]`, ...args);
          }
        },
      },
      edge: {
        debug: (...args: any[]): void => {
          if (isDevelopment) {
            const edgeContext = context ? `${context}:FLOW:EDGE` : 'FLOW:EDGE';
            console.debug(`[${edgeContext}]`, ...args);
          }
        },
      },
    },

    store: {
      debug: (...args: any[]): void => {
        if (isDevelopment) {
          const storeContext = context ? `${context}:STORE` : 'STORE';
          console.debug(`[${storeContext}]`, ...args);
        }
      },
    },

    performance: {
      debug: (...args: any[]): void => {
        if (isDevelopment) {
          const perfContext = context ? `${context}:PERFORMANCE` : 'PERFORMANCE';
          console.debug(`[${perfContext}]`, ...args);
        }
      },
    },

    // Create a sub-context logger
    withContext: (subContext: string): ContextualLogger => {
      const newContext = context ? `${context}:${subContext}` : subContext;
      return createLogger(newContext);
    },
  };
};

// Default logger instance (with auto-detection)
const logger = createLogger(null);

// Export factory function for creating contextual loggers
export const createContextLogger = (context: string): ContextualLogger => {
  return createLogger(context);
};

export default logger;
export type { Logger, LoggerMethod, SpecializedLogger, ContextualLogger };
